// Pure React SVG component for static flowchart rendering.
// No event handlers, no component state — safe for renderToStaticMarkup.
// Used by buildExportSVGString (svgExport.ts) for SVG/PNG export.

import React from 'react';
import type { Box, Arrow, Department, Area } from '../types/index';
import { getConnPt, getArrowWaypoints, segCrossing, BRIDGE_R, buildBridgedPath } from './arrowRouting';

export interface FlowchartSVGOptions {
  boxes: Box[];
  arrows: Arrow[];
  departments: Department[];
  areas: Area[];
  showLegend: boolean;
  showNotes: boolean;
  showCriticalPath: boolean;
  criticalPath: { boxIds: Set<number>; arrowIds: Set<number> };
  schedulingConflicts: { boxIds: Set<number>; arrowIds: Set<number> };
  /** Pre-computed end-date label per box ID (caller runs getEnd). */
  endLabels: Map<number, string>;
  /** Pre-computed start-date label per box ID (caller runs getStart). */
  startLabels: Map<number, string>;
  showStartDates?: boolean;
  showFinishDates?: boolean;
  /** Show the subitem-count / UID badges (see CanvasBox.tsx's own badges) —
   *  defaults to true (shown) when omitted, matching every other optional flag here. */
  showSubitemBadge?: boolean;
  showUidBadge?: boolean;
  showAreas?: boolean;
  /** Subitem count per box ID — needed since `boxes` here is already top-level-only. */
  subitemCountByBoxId?: Map<number, number>;
}

const LEGEND_W    = 180;
const LEGEND_ROW_H = 22;
const LEGEND_HDR_H = 28;
const PAD = 40;

export function computeSvgDimensions(opts: Pick<FlowchartSVGOptions, 'boxes' | 'departments' | 'showLegend' | 'areas'>) {
  const { boxes, departments, showLegend, areas } = opts;
  // Areas can extend beyond the boxes they surround, so their edges must be included
  // in the bounding box or the export viewBox clips the outer border.
  const minX = Math.min(...boxes.map(b => b.x), ...areas.map(a => a.x)) - PAD;
  const minY = Math.min(...boxes.map(b => b.y), ...areas.map(a => a.y)) - PAD;
  const maxX = Math.max(...boxes.map(b => b.x + b.width),  ...areas.map(a => a.x + a.width))  + PAD;
  const maxY = Math.max(...boxes.map(b => b.y + b.height), ...areas.map(a => a.y + a.height)) + PAD;
  const W = maxX - minX;
  const H = maxY - minY;
  const exportLegend = showLegend && departments.length > 0;
  const legendTotalH = exportLegend ? LEGEND_HDR_H + departments.length * LEGEND_ROW_H + 16 : 0;
  return {
    minX, minY, maxX, maxY, W, H,
    totalH: H + legendTotalH,
    legendX: Math.max(minX + PAD, maxX - LEGEND_W - PAD),
    legendY: maxY + 16,
    exportLegend,
  };
}

// Word-wrap helper for monospace SVG text
function wrapText(text: string, maxPx: number, fontSize: number): string[] {
  const charW = fontSize * 0.6;
  const maxChars = Math.max(1, Math.floor(maxPx / charW));
  const result: string[] = [];
  for (const rawLine of (text || '').split('\n')) {
    if (rawLine.length <= maxChars) { result.push(rawLine); continue; }
    const words = rawLine.split(' ');
    let cur = '';
    for (const word of words) {
      const test = cur ? cur + ' ' + word : word;
      if (test.length > maxChars && cur) { result.push(cur); cur = word; }
      else cur = test;
    }
    if (cur) result.push(cur);
  }
  return result.length ? result : [''];
}

// A note's live width auto-grows to keep its real (browser-measured) text height
// under `box.height - 24` (see CanvasBox.tsx's textarea onChange — 24 = the 12px
// top+bottom padding from the live `p-3` container). This wrapText() is only a
// fixed-average-character-width approximation of that same monospace font, so
// for some text it wraps into more lines than the live layout did at the same
// width, and those extra lines fell below the exported rect's bottom edge with
// nothing to stop them. Recomputing the same "lines × line-height + padding"
// relationship here and never rendering a callout shorter than that guarantees
// the exported box can never be too short for its own text, regardless of any
// remaining difference between this estimate and the real font metrics.
const CALLOUT_LINE_HEIGHT = 20;
const CALLOUT_VERTICAL_PADDING = 24; // matches CanvasBox.tsx's `maxHeight = box.height - 24`
export function calloutMinHeight(box: Box): number {
  const lines = wrapText(box.text || '', box.width - 20, 15);
  return lines.length * CALLOUT_LINE_HEIGHT + CALLOUT_VERTICAL_PADDING;
}

// ── Sub-renderers ─────────────────────────────────────────────────────────────

interface ArrowsProps {
  exportArrows: Arrow[];
  allArrows: Arrow[];
  boxes: Box[];
  criticalArrowIds: Set<number>;
  conflictArrowIds: Set<number>;
}
function SvgArrows({ exportArrows, boxes, criticalArrowIds, conflictArrowIds }: ArrowsProps) {
  return (
    <>
      {exportArrows.map((a, arrowIdx) => {
        const ra = a as any;
        const fb = boxes.find(b => b.id === ra.fromBox)!;
        const tb = boxes.find(b => b.id === ra.toBox)!;
        const isCallout = fb.type === 'callout' || tb.type === 'callout';

        let pathD: string;
        if (isCallout) {
          const fp = getConnPt(fb, ra.fromSide);
          const tp = getConnPt(tb, ra.toSide);
          pathD = `M ${fp.x} ${fp.y} L ${tp.x} ${tp.y}`;
        } else {
          const wpts = getArrowWaypoints(a, boxes);
          const crossingPts: { segIndex: number; t: number; x: number; y: number }[] = [];
          for (let j = 0; j < exportArrows.length; j++) {
            if (j === arrowIdx) continue;
            const other = exportArrows[j];
            const ro = other as any;
            const fb2 = boxes.find(b => b.id === ro.fromBox);
            const tb2 = boxes.find(b => b.id === ro.toBox);
            if (!fb2 || !tb2 || fb2.type === 'callout' || tb2.type === 'callout') continue;
            const owpts = getArrowWaypoints(other, boxes);
            for (let si = 0; si < wpts.length - 1; si++) {
              if (Math.abs(wpts[si + 1].y - wpts[si].y) >= Math.abs(wpts[si + 1].x - wpts[si].x)) continue;
              for (let sj = 0; sj < owpts.length - 1; sj++) {
                const cross = segCrossing(
                  wpts[si].x, wpts[si].y, wpts[si + 1].x, wpts[si + 1].y,
                  owpts[sj].x, owpts[sj].y, owpts[sj + 1].x, owpts[sj + 1].y,
                );
                if (cross) {
                  const tooClose = [wpts[si], wpts[si + 1]].some(
                    w => Math.sqrt((cross.x - w.x) ** 2 + (cross.y - w.y) ** 2) < BRIDGE_R * 2,
                  );
                  if (!tooClose) crossingPts.push({ segIndex: si, t: cross.t, x: cross.x, y: cross.y });
                }
              }
            }
          }
          pathD = buildBridgedPath(wpts, crossingPts);
        }

        const isCritical = criticalArrowIds.has(a.id);
        const tp = getConnPt(tb, ra.toSide);
        const exX = tp.x + (ra.toSide === 'left' ? -20 : ra.toSide === 'right' ? 20 : 0);
        const exY = tp.y + (ra.toSide === 'top'  ? -20 : ra.toSide === 'bottom' ? 20 : 0);

        return (
          <g key={a.id}>
            <path
              d={pathD}
              fill="none"
              stroke={isCallout ? '#eab308' : isCritical ? '#ef4444' : '#3b82f6'}
              strokeWidth={isCritical ? 2.5 : 2}
              strokeDasharray={a.dashed ? '6,3' : undefined}
              markerEnd={isCallout ? undefined : `url(#${isCritical ? 'arr-red' : 'arr'})`}
            />
            {!isCallout && conflictArrowIds.has(a.id) && (
              <g>
                <circle cx={exX} cy={exY} r={8} fill="#ef4444" />
                <text x={exX} y={exY} textAnchor="middle" dominantBaseline="central"
                  fontSize={13} fontWeight="bold" fill="white" fontFamily="sans-serif">!</text>
              </g>
            )}
          </g>
        );
      })}
    </>
  );
}

interface BoxProps {
  box: Box;
  isCritical: boolean;
  conflictBoxIds: Set<number>;
  endLabel: string;
  startLabel: string;
  showStartDates: boolean;
  showFinishDates: boolean;
  subitemCount: number;
  showSubitemBadge: boolean;
  showUidBadge: boolean;
}
function SvgBox({ box, isCritical, conflictBoxIds, endLabel, startLabel, showStartDates, showFinishDates, subitemCount, showSubitemBadge, showUidBadge }: BoxProps) {
  if (box.type === 'startday') {
    return (
      <g>
        <ellipse
          cx={box.x + box.width / 2} cy={box.y + box.height / 2}
          rx={box.width / 2} ry={box.height / 2}
          fill="#eff6ff" stroke="#3b82f6" strokeWidth={2}
        />
        <text x={box.x + box.width / 2} y={box.y + box.height / 2}
          textAnchor="middle" dominantBaseline="middle"
          fontSize={16} fontWeight="bold" fill="#1d4ed8" fontFamily="sans-serif">
          {box.text || ''}
        </text>
      </g>
    );
  }

  if (box.type === 'callout') {
    const lh = 20;
    const lines = wrapText(box.text || '', box.width - 20, 15);
    const ty = box.y + 12 + lh * 0.75;
    return (
      <g>
        <rect x={box.x} y={box.y} width={box.width} height={box.height}
          rx={8} fill="white" stroke="#eab308" strokeWidth={2} strokeDasharray="6,3" />
        {lines.map((line, i) => (
          <text key={i} x={box.x + 10} y={ty + i * lh}
            fontSize={15} fill="#1f2937" fontFamily="monospace">{line}</text>
        ))}
      </g>
    );
  }

  // Regular box
  const fill = box.departmentColor || '#dcfce7';
  const stroke = isCritical ? '#ef4444' : '#3b82f6';
  const strokeWidth = isCritical ? 3 : 2;
  const lh = 20;
  const hasBottom = !!(box.duration || box.assignee || box.scheduledStart);
  const textLines = wrapText(box.text || '', box.width - 16, 16);
  const startY = box.y + 12 + lh * 0.85;
  const pct = box.completion || 0;

  // Duration/assignee on-box labels (below). The live canvas renders these as two
  // fixed-width <input> elements side by side, so a long comma-separated resource
  // list in box.assignee just clips/scrolls inside its own input box and never
  // visually collides with the duration field next to it. Here they're raw <text>
  // elements instead — duration left-anchored growing right, assignee right-anchored
  // (textAnchor="end") growing left — so with enough resources assigned, assignee
  // grows leftward far enough to overlap/run over the duration text. Same class of
  // bug and same fix as the Gantt export row-label truncation above: estimate the
  // rendered width from a character count (6.5px/char at font-size 12, this file's
  // established sans-serif estimate) and truncate the STRING itself, since an SVG
  // clip-path isn't reliably honored by every PNG-rasterization path.
  const assigneeGapPx = box.duration ? 8 : 0;
  const durationWidthPx = (box.duration || '').length * 6.5;
  const assigneeMaxPx = Math.max(20, box.width - 12 - durationWidthPx - assigneeGapPx);
  const assigneeMaxChars = Math.max(3, Math.floor(assigneeMaxPx / 6.5));
  const assigneeRaw = box.assignee || '';
  const assigneeDisp = assigneeRaw.length > assigneeMaxChars
    ? assigneeRaw.slice(0, assigneeMaxChars - 1) + '…'
    : assigneeRaw;

  // End-date badge
  const badgeFill = box.importantFinish ? '#a855f7' : '#f3f4f6';
  const badgeTextColor = box.importantFinish ? '#ffffff' : '#4b5563';
  const badgeTw = endLabel ? endLabel.length * 6 + 8 : 0;
  const bx2 = box.x + box.width - badgeTw;
  const by2 = box.y + box.height + 2;

  // Status badge — top-left on the box border, matching live canvas (-top-3 -left-3, w-6 h-6)
  const statusSym = box.status === 'blocked' ? '!' : box.status === 'on-hold' ? '~' : '';
  const statusFill = box.status === 'blocked' ? '#fee2e2' : '#fef9c3';
  const statusStroke = box.status === 'blocked' ? '#ef4444' : '#eab308';
  const statusText = box.status === 'blocked' ? '#dc2626' : '#a16207';

  // Completion circle — top-left area on the box border, matching live canvas (left:30px, -top-3, w-6 h-6)
  // Live: button top-left at (box.x+30, box.y-12), size 24×24 → center at (box.x+42, box.y)
  const ccx = box.x + 42;
  const ccy = box.y;
  const cr = 10;

  // Start date badge
  const showStart = !!startLabel && (showStartDates || !!box.scheduledStart || !!box.earliestStart);
  const sTw = startLabel ? startLabel.length * 6 + 8 : 0;
  const startByTop = box.y + box.height + 2;

  return (
    <g>
      <rect x={box.x} y={box.y} width={box.width} height={box.height}
        rx={8} fill={fill} stroke={stroke} strokeWidth={strokeWidth} />

      {textLines.map((line, i) => (
        <text key={i} x={box.x + box.width / 2} y={startY + i * lh}
          textAnchor="middle" fontSize={16} fontWeight={600}
          fill="#1f2937" fontFamily="monospace">{line}</text>
      ))}

      {hasBottom && box.duration && (
        <text x={box.x + 6} y={box.y + box.height - 7}
          fontSize={12} fill="#6b7280" fontFamily="sans-serif">{box.duration}</text>
      )}
      {hasBottom && box.assignee && (
        <text x={box.x + box.width - 6} y={box.y + box.height - 7}
          textAnchor="end" fontSize={12} fill="#6b7280" fontFamily="sans-serif">{assigneeDisp}</text>
      )}

      {showFinishDates && box.duration && endLabel && (
        <g>
          <rect x={bx2} y={by2} width={badgeTw} height={16} rx={3} fill={badgeFill} />
          {conflictBoxIds.has(box.id) && (
            <rect x={bx2 - 2} y={by2 - 2} width={badgeTw + 4} height={20} rx={4}
              fill="none" stroke="#ef4444" strokeWidth={2.5} />
          )}
          <text x={box.x + box.width - 2} y={by2 + 12}
            textAnchor="end" fontSize={12} fill={badgeTextColor} fontFamily="sans-serif">{endLabel}</text>
        </g>
      )}

      {statusSym && (
        <g>
          {/* White border ring (border-2 border-white in live) */}
          <rect x={box.x - 14} y={box.y - 14} width={28} height={28} rx={5}
            fill="white" />
          <rect x={box.x - 12} y={box.y - 12} width={24} height={24} rx={4}
            fill={statusFill} stroke={statusStroke} strokeWidth={1.5} />
          <text x={box.x} y={box.y} textAnchor="middle" dominantBaseline="central"
            fontSize={14} fontWeight={700} fill={statusText} fontFamily="sans-serif">{statusSym}</text>
        </g>
      )}

      {pct > 0 && (
        <g>
          {/* White outer ring mimics the button's bg-white + border-2 border-gray-300 */}
          <circle cx={ccx} cy={ccy} r={12} fill="white" stroke="#d1d5db" strokeWidth={2} />
          {pct >= 100 ? (
            <g>
              <circle cx={ccx} cy={ccy} r={cr} fill="#10b981" />
              <path d={`M${ccx - 5} ${ccy} L${ccx - 1.5} ${ccy + 3.5} L${ccx + 5} ${ccy - 3}`}
                stroke="white" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" fill="none" />
            </g>
          ) : (
            <g>
              {pct >= 25 && <path d={`M${ccx} ${ccy} L${ccx} ${ccy - cr} A${cr} ${cr} 0 0 1 ${ccx + cr} ${ccy} Z`} fill="#10b981" />}
              {pct >= 50 && <path d={`M${ccx} ${ccy} L${ccx + cr} ${ccy} A${cr} ${cr} 0 0 1 ${ccx} ${ccy + cr} Z`} fill="#10b981" />}
              {pct >= 75 && <path d={`M${ccx} ${ccy} L${ccx} ${ccy + cr} A${cr} ${cr} 0 0 1 ${ccx - cr} ${ccy} Z`} fill="#10b981" />}
            </g>
          )}
        </g>
      )}

      {showStart && (
        <g>
          {box.scheduledStart ? (
            <>
              <rect x={box.x + 2} y={startByTop} width={sTw} height={16} rx={3}
                fill="#fffbeb" stroke="#fde68a" strokeWidth={1} />
              <text x={box.x + 6} y={startByTop + 12} fontSize={12} fill="#d97706" fontFamily="sans-serif">{startLabel}</text>
            </>
          ) : box.earliestStart ? (
            <>
              <rect x={box.x + 2} y={startByTop} width={sTw} height={16} rx={3}
                fill="#eff6ff" stroke="#bfdbfe" strokeWidth={1} />
              <text x={box.x + 6} y={startByTop + 12} fontSize={12} fill="#2563eb" fontFamily="sans-serif">{startLabel}</text>
            </>
          ) : (
            <>
              <rect x={box.x + 2} y={startByTop} width={sTw} height={16} rx={3}
                fill="#f3f4f6" stroke="#e5e7eb" strokeWidth={1} />
              <text x={box.x + 6} y={startByTop + 12} fontSize={12} fill="#6b7280" fontFamily="sans-serif">{startLabel}</text>
            </>
          )}
        </g>
      )}

      {/* 24/7 badge — top-right corner, matching live (top: -12px, right: -12px, bg: #7c3aed) */}
      {box.alwaysOn && (
        <g>
          <rect x={box.x + box.width - 14} y={box.y - 14} width={28} height={14} rx={4} fill="white" />
          <rect x={box.x + box.width - 12} y={box.y - 12} width={24} height={10} rx={3} fill="#7c3aed" />
          <text x={box.x + box.width} y={box.y - 7} textAnchor="middle" dominantBaseline="central"
            fontSize={7} fontWeight={700} fill="white" fontFamily="sans-serif">24/7</text>
        </g>
      )}
      {/* 24/5 badge — left of 24/7 when both active, else same corner */}
      {box.is24_5 && (
        <g>
          <rect x={box.alwaysOn ? box.x + box.width - 40 : box.x + box.width - 14} y={box.y - 14} width={28} height={14} rx={4} fill="white" />
          <rect x={box.alwaysOn ? box.x + box.width - 38 : box.x + box.width - 12} y={box.y - 12} width={24} height={10} rx={3} fill="#0891b2" />
          <text x={box.alwaysOn ? box.x + box.width - 26 : box.x + box.width} y={box.y - 7} textAnchor="middle" dominantBaseline="central"
            fontSize={7} fontWeight={700} fill="white" fontFamily="sans-serif">24/5</text>
        </g>
      )}
      {/* Subitem-count badge — top-right, below 24/5/24/7 (matching CanvasBox.tsx: top: 0.6rem, right: -0.75rem) */}
      {showSubitemBadge && subitemCount > 0 && (() => {
        const w = String(subitemCount).length * 6 + 10;
        const y = box.y + 9.6;
        return (
          <g>
            <rect x={box.x + box.width - w + 12} y={y - 2} width={w} height={14} rx={4} fill="white" />
            <rect x={box.x + box.width - w + 14} y={y} width={w - 4} height={10} rx={3} fill="#4b5563" />
            <text x={box.x + box.width + 12 - 2} y={y + 5} textAnchor="middle" dominantBaseline="central"
              fontSize={7} fontWeight={700} fill="white" fontFamily="sans-serif">{subitemCount}</text>
          </g>
        );
      })()}
      {/* UID badge — top-right, below the subitem-count badge (matching CanvasBox.tsx: top: 1.7rem, right: -0.75rem) */}
      {showUidBadge && box.uid && (() => {
        const w = box.uid!.length * 6 + 10;
        const y = box.y + 27.2;
        return (
          <g>
            <rect x={box.x + box.width - w + 12} y={y - 2} width={w} height={14} rx={4} fill="white" />
            <rect x={box.x + box.width - w + 14} y={y} width={w - 4} height={10} rx={3} fill="#9ca3af" />
            <text x={box.x + box.width + 12 - 2} y={y + 5} textAnchor="middle" dominantBaseline="central"
              fontSize={7} fontWeight={700} fill="white" fontFamily="sans-serif">{box.uid}</text>
          </g>
        );
      })()}
    </g>
  );
}

// ── Main component ────────────────────────────────────────────────────────────

export function FlowchartSvgScene(props: FlowchartSVGOptions): React.ReactElement {
  const {
    arrows, departments, areas,
    showNotes, showCriticalPath,
    criticalPath, schedulingConflicts, endLabels, startLabels, showStartDates, showFinishDates,
    showSubitemBadge, showUidBadge, subitemCountByBoxId, showAreas,
  } = props;

  // Never render (or size the canvas for) a note shorter than its own wrapped text
  // needs — see calloutMinHeight's comment. Applied once here so the bounding-box
  // calc, arrow connection points, and the box's own rect all agree on one height.
  const boxes = props.boxes.map(b => b.type === 'callout' ? { ...b, height: Math.max(b.height, calloutMinHeight(b)) } : b);

  const { minX, minY, W, H, totalH, legendX, legendY, exportLegend } = computeSvgDimensions({ ...props, boxes });

  const criticalArrowIds = showCriticalPath ? criticalPath.arrowIds : new Set<number>();
  const criticalBoxIds   = showCriticalPath ? criticalPath.boxIds   : new Set<number>();

  const exportArrows = arrows.filter(a => {
    const ra = a as any;
    const fb = boxes.find(b => b.id === ra.fromBox);
    const tb = boxes.find(b => b.id === ra.toBox);
    if (!fb || !tb) return false;
    if (!showNotes && (fb.type === 'callout' || tb.type === 'callout')) return false;
    return true;
  });

  return (
    <svg xmlns="http://www.w3.org/2000/svg" width={W} height={totalH} viewBox={`${minX} ${minY} ${W} ${totalH}`}>
      <defs>
        <marker id="arr" markerWidth="8" markerHeight="6" refX="8" refY="3" orient="auto">
          <polygon points="0 0,8 3,0 6" fill="#3b82f6" />
        </marker>
        <marker id="arr-red" markerWidth="8" markerHeight="6" refX="8" refY="3" orient="auto">
          <polygon points="0 0,8 3,0 6" fill="#ef4444" />
        </marker>
      </defs>

      <rect x={minX} y={minY} width={W} height={H} fill="white" />

      {showAreas !== false && areas.map((area, i) => (
        <g key={i}>
          <rect x={area.x} y={area.y} width={area.width} height={area.height}
            fill="none" stroke={area.color} strokeWidth={2} strokeDasharray="10,6" />
          {area.title && (
            <text x={area.x + 12} y={area.y + 34} fontSize={22} fontWeight={700}
              fill={area.color} fontFamily="sans-serif">{area.title}</text>
          )}
        </g>
      ))}

      <SvgArrows
        exportArrows={exportArrows}
        allArrows={arrows}
        boxes={boxes}
        criticalArrowIds={criticalArrowIds}
        conflictArrowIds={schedulingConflicts.arrowIds}
      />

      {boxes
        .filter(box => showNotes || box.type !== 'callout')
        .map(box => (
          <SvgBox
            key={box.id}
            box={box}
            isCritical={criticalBoxIds.has(box.id)}
            conflictBoxIds={schedulingConflicts.boxIds}
            endLabel={endLabels.get(box.id) || ''}
            startLabel={startLabels ? startLabels.get(box.id) || '' : ''}
            showStartDates={showStartDates !== false}
            showFinishDates={showFinishDates !== false}
            subitemCount={subitemCountByBoxId?.get(box.id) || 0}
            showSubitemBadge={showSubitemBadge !== false}
            showUidBadge={showUidBadge !== false}
          />
        ))
      }

      {exportLegend && (
        <g>
          <rect x={legendX} y={legendY} width={LEGEND_W}
            height={LEGEND_HDR_H + departments.length * LEGEND_ROW_H}
            rx={6} fill="white" stroke="#94a3b8" strokeWidth={1.5} />
          <line x1={legendX} y1={legendY + LEGEND_HDR_H}
            x2={legendX + LEGEND_W} y2={legendY + LEGEND_HDR_H}
            stroke="#e5e7eb" strokeWidth={1} />
          <text x={legendX + LEGEND_W / 2} y={legendY + 18}
            textAnchor="middle" fontSize={12} fontWeight={700}
            fill="#1f2937" fontFamily="sans-serif">Categories</text>
          {departments.map((dept, i) => {
            const ry = legendY + LEGEND_HDR_H + i * LEGEND_ROW_H;
            return (
              <g key={i}>
                <rect x={legendX + 10} y={ry + 4} width={14} height={14} rx={3}
                  fill={dept.color} stroke="#d1d5db" strokeWidth={1} />
                <text x={legendX + 32} y={ry + 14}
                  fontSize={12} fill="#374151" fontFamily="sans-serif">{dept.name || ''}</text>
              </g>
            );
          })}
        </g>
      )}
    </svg>
  );
}
